import * as THREE from 'three';

/**
 * Compute face normals for a geometry
 * Returns an array where each face (triangle) has its normal stored
 */
export function computeFaceNormals(geometry) {
  const positions = geometry.attributes.position.array;
  const indices = geometry.index ? geometry.index.array : null;
  const vertexCount = positions.length / 3;

  const faceNormals = [];
  const v1 = new THREE.Vector3();
  const v2 = new THREE.Vector3();
  const v3 = new THREE.Vector3();
  const cb = new THREE.Vector3();
  const ab = new THREE.Vector3();

  if (indices) {
    // Indexed geometry
    for (let i = 0; i < indices.length; i += 3) {
      const i1 = indices[i] * 3;
      const i2 = indices[i + 1] * 3;
      const i3 = indices[i + 2] * 3;

      v1.set(positions[i1], positions[i1 + 1], positions[i1 + 2]);
      v2.set(positions[i2], positions[i2 + 1], positions[i2 + 2]);
      v3.set(positions[i3], positions[i3 + 1], positions[i3 + 2]);

      cb.subVectors(v3, v2);
      ab.subVectors(v1, v2);
      cb.cross(ab);
      cb.normalize();

      faceNormals.push(cb.clone());
    }
  } else {
    // Non-indexed geometry
    for (let i = 0; i < vertexCount; i += 3) {
      const i1 = i * 3;
      const i2 = (i + 1) * 3;
      const i3 = (i + 2) * 3;

      v1.set(positions[i1], positions[i1 + 1], positions[i1 + 2]);
      v2.set(positions[i2], positions[i2 + 1], positions[i2 + 2]);
      v3.set(positions[i3], positions[i3 + 1], positions[i3 + 2]);

      cb.subVectors(v3, v2);
      ab.subVectors(v1, v2);
      cb.cross(ab);
      cb.normalize();

      faceNormals.push(cb.clone());
    }
  }

  return faceNormals;
}

/**
 * Create a mapping from vertex index to the faces that use it
 */
export function buildVertexToFaceMap(geometry) {
  const indices = geometry.index ? geometry.index.array : null;
  const vertexCount = geometry.attributes.position.count;
  const vertexToFaces = new Map();

  // Initialize map
  for (let i = 0; i < vertexCount; i++) {
    vertexToFaces.set(i, []);
  }

  if (indices) {
    for (let i = 0; i < indices.length; i += 3) {
      const faceIndex = Math.floor(i / 3);
      vertexToFaces.get(indices[i]).push(faceIndex);
      vertexToFaces.get(indices[i + 1]).push(faceIndex);
      vertexToFaces.get(indices[i + 2]).push(faceIndex);
    }
  } else {
    for (let i = 0; i < vertexCount; i += 3) {
      const faceIndex = Math.floor(i / 3);
      vertexToFaces.get(i).push(faceIndex);
      vertexToFaces.get(i + 1).push(faceIndex);
      vertexToFaces.get(i + 2).push(faceIndex);
    }
  }

  return vertexToFaces;
}

/**
 * Compute better normals for vertices by using face normals with edge detection
 * This prevents averaging across sharp edges
 */
/**
 * Compute normals with special handling for boundary vertices
 * For boundary vertices: use area-weighted average of adjacent face normals
 * For interior vertices: use standard averaging with angle threshold
 */
export function computeSmartVertexNormals(geometry, sharpEdgeThreshold = 30) {
  const positions = geometry.attributes.position.array;
  const indices = geometry.index ? geometry.index.array : null;
  const vertexCount = positions.length / 3;

  if (!indices) {
    // For non-indexed geometry, fall back to standard normal computation
    geometry.computeVertexNormals();
    return geometry.attributes.normal.array.slice();
  }

  // Compute face normals and areas
  const faceNormals = [];
  const faceAreas = [];
  const v0 = new THREE.Vector3();
  const v1 = new THREE.Vector3();
  const v2 = new THREE.Vector3();
  const cb = new THREE.Vector3();
  const ab = new THREE.Vector3();

  for (let i = 0; i < indices.length; i += 3) {
    const i0 = indices[i] * 3;
    const i1 = indices[i + 1] * 3;
    const i2 = indices[i + 2] * 3;

    v0.set(positions[i0], positions[i0 + 1], positions[i0 + 2]);
    v1.set(positions[i1], positions[i1 + 1], positions[i1 + 2]);
    v2.set(positions[i2], positions[i2 + 1], positions[i2 + 2]);

    cb.subVectors(v2, v1);
    ab.subVectors(v0, v1);
    cb.cross(ab);

    const area = cb.length() / 2;
    cb.normalize();

    faceNormals.push(cb.clone());
    faceAreas.push(area);
  }

  // Build vertex to face mapping
  const vertexToFaces = buildVertexToFaceMap(geometry);

  // Find boundary edges
  const edgeCount = new Map();
  const edgeToFace = new Map(); // Track which face each edge belongs to

  for (let i = 0; i < indices.length; i += 3) {
    const faceIdx = Math.floor(i / 3);
    const edges = [
      [indices[i], indices[i + 1]],
      [indices[i + 1], indices[i + 2]],
      [indices[i + 2], indices[i]]
    ];

    edges.forEach(([a, b]) => {
      const key = `${Math.min(a, b)}-${Math.max(a, b)}`;
      edgeCount.set(key, (edgeCount.get(key) || 0) + 1);

      if (!edgeToFace.has(key)) {
        edgeToFace.set(key, []);
      }
      edgeToFace.get(key).push(faceIdx);
    });
  }

  const boundaryVertices = new Set();
  const boundaryEdges = new Map(); // vertex -> boundary edges it's part of

  for (const [key, count] of edgeCount) {
    if (count === 1) {
      const [a, b] = key.split('-').map(Number);
      boundaryVertices.add(a);
      boundaryVertices.add(b);

      // Store boundary edges for each vertex
      if (!boundaryEdges.has(a)) boundaryEdges.set(a, []);
      if (!boundaryEdges.has(b)) boundaryEdges.set(b, []);

      const faces = edgeToFace.get(key);
      boundaryEdges.get(a).push({ edge: [a, b], faces });
      boundaryEdges.get(b).push({ edge: [a, b], faces });
    }
  }

  // Compute vertex normals
  const smartNormals = new Float32Array(vertexCount * 3);

  for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
    const faces = vertexToFaces.get(vertexIndex);

    if (!faces || faces.length === 0) {
      smartNormals[vertexIndex * 3] = 0;
      smartNormals[vertexIndex * 3 + 1] = 1;
      smartNormals[vertexIndex * 3 + 2] = 0;
      continue;
    }

    // For ALL vertices, use area-weighted average
    // This prevents stepping by giving more weight to larger faces
    const averagedNormal = new THREE.Vector3();

    for (const faceIndex of faces) {
      const faceNormal = faceNormals[faceIndex];
      const faceArea = faceAreas[faceIndex];

      // Weight by face area to get smoother results
      averagedNormal.add(faceNormal.clone().multiplyScalar(faceArea));
    }

    averagedNormal.normalize();

    smartNormals[vertexIndex * 3] = averagedNormal.x;
    smartNormals[vertexIndex * 3 + 1] = averagedNormal.y;
    smartNormals[vertexIndex * 3 + 2] = averagedNormal.z;
  }

  return smartNormals;
}

/**
 * Apply Laplacian smoothing to boundary vertices to eliminate stepping artifacts
 * This smooths the positions of boundary vertices while keeping interior vertices fixed
 */
export function smoothBoundaryVertices(positions, indices, iterations = 3, lambda = 0.5) {
  const vertexCount = positions.length / 3;

  // Find boundary edges
  const edgeCount = new Map();
  for (let i = 0; i < indices.length; i += 3) {
    const edges = [
      [indices[i], indices[i + 1]],
      [indices[i + 1], indices[i + 2]],
      [indices[i + 2], indices[i]]
    ];

    edges.forEach(([a, b]) => {
      const key = `${Math.min(a, b)}-${Math.max(a, b)}`;
      edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
    });
  }

  // Identify boundary vertices and their neighbors
  const boundaryVertices = new Set();
  const vertexNeighbors = new Map();

  for (let i = 0; i < vertexCount; i++) {
    vertexNeighbors.set(i, new Set());
  }

  for (const [key, count] of edgeCount) {
    const [a, b] = key.split('-').map(Number);

    // Add as neighbors regardless of boundary status
    vertexNeighbors.get(a).add(b);
    vertexNeighbors.get(b).add(a);

    if (count === 1) {
      boundaryVertices.add(a);
      boundaryVertices.add(b);
    }
  }

  // Apply Laplacian smoothing only to boundary vertices
  const smoothedPositions = new Float32Array(positions);

  for (let iter = 0; iter < iterations; iter++) {
    const newPositions = new Float32Array(smoothedPositions);

    for (const vertexIndex of boundaryVertices) {
      const neighbors = vertexNeighbors.get(vertexIndex);
      if (!neighbors || neighbors.size === 0) continue;

      let avgX = 0, avgY = 0, avgZ = 0;
      let count = 0;

      // Average position of neighboring boundary vertices only
      for (const neighborIdx of neighbors) {
        if (boundaryVertices.has(neighborIdx)) {
          avgX += smoothedPositions[neighborIdx * 3];
          avgY += smoothedPositions[neighborIdx * 3 + 1];
          avgZ += smoothedPositions[neighborIdx * 3 + 2];
          count++;
        }
      }

      if (count > 0) {
        avgX /= count;
        avgY /= count;
        avgZ /= count;

        // Blend between original and averaged position
        const i3 = vertexIndex * 3;
        newPositions[i3] = smoothedPositions[i3] * (1 - lambda) + avgX * lambda;
        newPositions[i3 + 1] = smoothedPositions[i3 + 1] * (1 - lambda) + avgY * lambda;
        newPositions[i3 + 2] = smoothedPositions[i3 + 2] * (1 - lambda) + avgZ * lambda;
      }
    }

    smoothedPositions.set(newPositions);
  }

  return smoothedPositions;
}

/**
 * Create shell with face-based displacement to avoid stepping artifacts
 */
export function createShellWithFaceNormals(baseGeometry, basePositions, thickness) {
  if (!baseGeometry || !basePositions || thickness <= 0) return null;

  const positions = baseGeometry.attributes.position.array;
  const indices = baseGeometry.index ? baseGeometry.index.array : null;

  if (!indices) {
    console.warn('Geometry must be indexed for face-based shell creation');
    return null;
  }

  // Compute face normals
  const faceNormals = computeFaceNormals(baseGeometry);

  // Build vertex to face mapping
  const vertexToFaces = buildVertexToFaceMap(baseGeometry);

  // For each unique vertex, we'll create multiple displaced versions
  // (one for each unique face normal direction it's part of)
  const newPositions = [];
  const newIndices = [];

  // Map from (originalVertexIndex, faceIndex) to new vertex index
  const vertexFaceMap = new Map();
  let currentVertexIndex = 0;

  // Process each face
  for (let faceIdx = 0; faceIdx < indices.length / 3; faceIdx++) {
    const i0 = indices[faceIdx * 3];
    const i1 = indices[faceIdx * 3 + 1];
    const i2 = indices[faceIdx * 3 + 2];
    const faceNormal = faceNormals[faceIdx];

    const vertexIndices = [i0, i1, i2];
    const newFaceIndices = [];

    // For each vertex in this face
    for (const origIdx of vertexIndices) {
      const key = `${origIdx}_${faceIdx}`;

      if (!vertexFaceMap.has(key)) {
        // Create new vertex displaced along face normal
        const x = basePositions[origIdx * 3];
        const y = basePositions[origIdx * 3 + 1];
        const z = basePositions[origIdx * 3 + 2];

        // Outer vertex (displaced along face normal)
        newPositions.push(
          x + faceNormal.x * thickness,
          y + faceNormal.y * thickness,
          z + faceNormal.z * thickness
        );

        vertexFaceMap.set(key, currentVertexIndex);
        newFaceIndices.push(currentVertexIndex);
        currentVertexIndex++;

        // Inner vertex (original position) - we'll add these after
      } else {
        newFaceIndices.push(vertexFaceMap.get(key));
      }
    }

    // Add outer face
    newIndices.push(newFaceIndices[0], newFaceIndices[1], newFaceIndices[2]);
  }

  // Now add inner surface (original positions)
  const innerVertexMap = new Map();
  const innerStartIndex = currentVertexIndex;

  for (let faceIdx = 0; faceIdx < indices.length / 3; faceIdx++) {
    const i0 = indices[faceIdx * 3];
    const i1 = indices[faceIdx * 3 + 1];
    const i2 = indices[faceIdx * 3 + 2];

    const vertexIndices = [i0, i1, i2];
    const newFaceIndices = [];

    for (const origIdx of vertexIndices) {
      const key = `${origIdx}_${faceIdx}`;

      if (!innerVertexMap.has(key)) {
        const x = basePositions[origIdx * 3];
        const y = basePositions[origIdx * 3 + 1];
        const z = basePositions[origIdx * 3 + 2];

        newPositions.push(x, y, z);

        innerVertexMap.set(key, currentVertexIndex);
        newFaceIndices.push(currentVertexIndex);
        currentVertexIndex++;
      } else {
        newFaceIndices.push(innerVertexMap.get(key));
      }
    }

    // Add inner face (reversed winding)
    newIndices.push(newFaceIndices[2], newFaceIndices[1], newFaceIndices[0]);
  }

  // Add side walls at boundaries
  const boundaryEdges = findBoundaryEdges(indices);

  for (const [a, b] of boundaryEdges) {
    // Find faces that use this edge to get proper vertex indices
    const facesUsingEdge = [];

    for (let faceIdx = 0; faceIdx < indices.length / 3; faceIdx++) {
      const i0 = indices[faceIdx * 3];
      const i1 = indices[faceIdx * 3 + 1];
      const i2 = indices[faceIdx * 3 + 2];

      if ((i0 === a && i1 === b) || (i1 === a && i2 === b) || (i2 === a && i0 === b) ||
          (i0 === b && i1 === a) || (i1 === b && i2 === a) || (i2 === b && i0 === a)) {
        facesUsingEdge.push(faceIdx);
      }
    }

    if (facesUsingEdge.length > 0) {
      const faceIdx = facesUsingEdge[0];

      const outerA = vertexFaceMap.get(`${a}_${faceIdx}`);
      const outerB = vertexFaceMap.get(`${b}_${faceIdx}`);
      const innerA = innerVertexMap.get(`${a}_${faceIdx}`);
      const innerB = innerVertexMap.get(`${b}_${faceIdx}`);

      if (outerA !== undefined && outerB !== undefined && innerA !== undefined && innerB !== undefined) {
        // Create side wall quad
        newIndices.push(outerA, innerA, outerB);
        newIndices.push(outerB, innerA, innerB);
      }
    }
  }

  // Create geometry
  const shellGeometry = new THREE.BufferGeometry();
  shellGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
  shellGeometry.setIndex(newIndices);

  // Handle vertex colors if present
  if (baseGeometry.attributes.color) {
    const originalColors = baseGeometry.attributes.color.array;
    const newColors = [];

    // Add colors for outer vertices
    for (const [key, newIdx] of vertexFaceMap) {
      const origIdx = parseInt(key.split('_')[0]);
      newColors[newIdx * 3] = originalColors[origIdx * 3];
      newColors[newIdx * 3 + 1] = originalColors[origIdx * 3 + 1];
      newColors[newIdx * 3 + 2] = originalColors[origIdx * 3 + 2];
    }

    // Add colors for inner vertices
    for (const [key, newIdx] of innerVertexMap) {
      const origIdx = parseInt(key.split('_')[0]);
      newColors[newIdx * 3] = originalColors[origIdx * 3];
      newColors[newIdx * 3 + 1] = originalColors[origIdx * 3 + 1];
      newColors[newIdx * 3 + 2] = originalColors[origIdx * 3 + 2];
    }

    shellGeometry.setAttribute('color', new THREE.Float32BufferAttribute(newColors, 3));
  }

  shellGeometry.computeVertexNormals();
  shellGeometry.computeBoundingSphere();

  return shellGeometry;
}

function findBoundaryEdges(indices) {
  const edgeCount = new Map();
  const orientedEdges = new Map();

  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i];
    const b = indices[i + 1];
    const c = indices[i + 2];

    const edges = [
      [a, b],
      [b, c],
      [c, a]
    ];

    edges.forEach(([v1, v2]) => {
      const minV = Math.min(v1, v2);
      const maxV = Math.max(v1, v2);
      const key = `${minV}-${maxV}`;
      const isForward = v1 < v2;

      if (!edgeCount.has(key)) {
        edgeCount.set(key, 0);
        orientedEdges.set(key, { forward: 0, backward: 0 });
      }

      edgeCount.set(key, edgeCount.get(key) + 1);
      const edgeInfo = orientedEdges.get(key);
      if (isForward) {
        edgeInfo.forward++;
      } else {
        edgeInfo.backward++;
      }
    });
  }

  const boundaryEdges = [];
  for (const [key, count] of edgeCount) {
    if (count === 1) {
      const [a, b] = key.split('-').map(Number);
      const edgeInfo = orientedEdges.get(key);

      if (edgeInfo.forward > 0) {
        boundaryEdges.push([a, b]);
      } else {
        boundaryEdges.push([b, a]);
      }
    }
  }

  return boundaryEdges;
}
